import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import BackLink from "../../components/navigation/BackLink/BackLink";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import ModalShell from "./ModalShell";
import StaffImportModal from "./StaffImportModal";
import { clampPageOnEmpty } from "../../utils/helpers/pagination";
import {
  hasPermissionAt,
  ROLE_CODES,
  USER_PERMISSION_CODES,
} from "../../utils/permissions";
import {
  createUser,
  deleteUser,
  getRoles,
  getUsers,
  inviteAllUsers,
  updateUser,
} from "../../services/userService";
import {
  createDepartment,
  deleteDepartment,
  getDepartments,
  getDomainSubjects,
  updateDepartment,
} from "../../services/organizationService";
import { resendInvitation } from "../../services/invitationService";
import { fullName } from "../../utils/helpers/userDisplay";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { Field, inputClass } from "../../components/forms/Field";
import FilterSelect from "../../components/forms/FilterSelect";
import useOrgScope from "../../utils/hooks/useOrgScope";
import MultiSelect from "../../components/forms/MultiSelect";

// Shared catch handling for the create/edit catalog fetches (departments /
// subjects / roles): ignore aborts, otherwise surface one toast so an empty
// picker reads as "couldn't load", not "this school has none". Returns true on
// a real error so the caller can also reset its list state.
const reportLoadError = (err, t) => {
  if (err?.code === "ERR_CANCELED") return false;
  toast.error(t("users.loadFailed"));
  return true;
};

// The account fields a directory can own. `managed_fields` also names
// StaffOrganization fields (organization, start_date, …) that no form on this
// page renders, so the forms ask about their own inputs, not the whole list.
const ACCOUNT_FIELDS = ["first_name", "last_name", "email"];

// A field the directory owns still has to be readable — the admin came here to
// look it up — but it is not an input, because typing in it can only earn a
// 400. Spelled out rather than layered on `inputClass()`: Tailwind resolves
// same-property conflicts by stylesheet order, not by string order.
const managedInputClass =
  "w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-100 text-slate-500 outline-none cursor-not-allowed";

const NO_ROLE_LOCKS = new Map();

const FilterIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 6h14M8 12h8M11 18h2" />
  </svg>
);

const SearchIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
  </svg>
);

// Multi-select role picker shared by the create + edit forms, built from the
// /auth/roles/ catalog (`roles`). Privileged roles render locked unless the
// requester holds accounts.roles.manage — locked-but-checked options keep
// their value in the form state, so saving never silently strips a role.
//
// `lockedRoles` extends that same mechanism to grants this screen cannot revoke
// at all: Map<code, {tag, title}>, where `tag` marks the chip with where the
// role comes from and `title` says it in words.
function RoleCheckboxGrid({
  register,
  roles,
  canManageRoles,
  disabled,
  lockedRoles = NO_ROLE_LOCKS,
  idPrefix = "role",
  paddingClass = "py-3",
}) {
  const { t } = useTranslation();
  return (
    <Field label={t("users.role")} required>
      <div className="grid grid-cols-2 gap-2">
        {roles.map((role) => {
          const lock = lockedRoles.get(role.code);
          const locked =
            disabled || Boolean(lock) || (role.is_privileged && !canManageRoles);
          return (
            <label
              key={role.code}
              className={locked ? "cursor-not-allowed" : "cursor-pointer"}
              htmlFor={`${idPrefix}-${role.code}`}
              title={lock?.title}
            >
              <input
                id={`${idPrefix}-${role.code}`}
                type="checkbox"
                value={role.code}
                disabled={locked}
                {...register("roles")}
                className="peer sr-only"
              />
              <div
                className={`flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 ${paddingClass} text-center text-sm font-bold text-slate-600 transition peer-checked:bg-indigo-600 peer-checked:border-indigo-600 peer-checked:text-white peer-checked:shadow-md peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500/40 ${
                  locked ? "opacity-60" : "hover:border-indigo-200"
                }`}
              >
                <span className="truncate">{role.name}</span>
                {/* `border-current` rather than a fixed colour: the chip flips
                    to white text when checked, and a nested span is not a
                    sibling of the peer, so peer-checked: can't reach it. */}
                {lock && (
                  <span className="shrink-0 rounded border border-current px-1 py-px text-[9px] font-black uppercase leading-none tracking-wide opacity-70">
                    {lock.tag}
                  </span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </Field>
  );
}

// One account field on the edit form. When the directory owns it the input
// becomes a read-only box naming where it changes — and its validation rules go
// with it, so a directory account with a blank surname can still have its roles
// saved instead of failing on a "required" the admin cannot satisfy.
function EditAccountField({
  label,
  name,
  type = "text",
  rules,
  error,
  register,
  directory,
}) {
  const { t } = useTranslation();
  if (!directory) {
    return (
      <Field label={label} error={error?.message}>
        <input type={type} {...register(name, rules)} className={inputClass(error)} />
      </Field>
    );
  }
  return (
    <Field label={label} hint={t("users.managedField", { name: directory.name })}>
      <input type={type} readOnly {...register(name)} className={managedInputClass} />
    </Field>
  );
}

const getSchoolName = (user) =>
  user?.organizations?.[0]?.organization?.name || "—";

// Mirrors the backend StandardResultsSetPagination PAGE_SIZE (config.settings).
const USERS_PAGE_SIZE = 20;


// A row is selectable when there is an email to send to. An explicit tick
// carries the per-row "Send invite" semantics on the backend: an account that
// can already sign in gets a set-new-password link (the admin reset), a live
// invitation is re-issued with the old link revoked. Only the school-wide
// "select all pending" sweep keeps the cannot-sign-in-only rule. Email-less
// rows render the table's usual "—" (a disabled checkbox reads as broken).
//
// Our own row is never selectable: invite_all_pending excludes the acting
// account, so ticking it produced a phantom "not sent" every time. An admin who
// wants their own reset link uses the row's "Send invite" button, which does
// act on self.
//
// A directory-backed row is never selectable either: it signs in via the
// directory's own SSO and has no local password to set, and the bulk endpoint
// already excludes it (`~directory_backed_q()`) — a tick here could only ever
// end in the "missed" shortfall toast.
const isSelectable = (u, selfUuid) =>
  Boolean(u.email) && !u.managed_by && u.uuid !== selfUuid;

export default function Users() {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);

  // Scoped to the school being administered, not the union: someone who
  // administers another school and only teaches at this one holds the
  // permission but must not be offered the controls here. The backend enforces
  // the same boundary; this stops the UI from promising what it will refuse.
  //
  // For a head-office account the school is whichever one the app header is
  // pointed at — `organizations[0]` is the district root there, which holds no
  // teachers, no departments, and is not a school anyone administers.
  const {
    canCompareSchools,
    organizations: scopeOrganizations,
    effectiveOrgUuid,
    scopeMode,
  } = useOrgScope();
  const scopedOrgUuid =
    effectiveOrgUuid || user?.organizations?.[0]?.organization?.uuid;
  // The header is pointed at a head org: this list spans several schools, so
  // anything that acts on "the school" has no single answer yet.
  const isNetworkScope = canCompareSchools && scopeMode === "network";
  const canManage = hasPermissionAt(
    user, USER_PERMISSION_CODES.MANAGE, scopedOrgUuid,
  );
  const canManageRoles = hasPermissionAt(
    user, USER_PERMISSION_CODES.ROLES_MANAGE, scopedOrgUuid,
  );

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showDepartments, setShowDepartments] = useState(false);
  const [invitingUuid, setInvitingUuid] = useState(null);
  const [invitingAll, setInvitingAll] = useState(false);
  // Selection is a Set of uuids, so it survives pagination and searches; rows
  // are re-identified by uuid whichever page they render on. `allPending` is
  // the django-admin "select all matching" mode — no uuids, the backend's own
  // everyone-pending sweep.
  const [selected, setSelected] = useState(() => new Set());
  const [allPending, setAllPending] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);
  const scopedOrg = scopeOrganizations.find((o) => o.uuid === scopedOrgUuid);
  const school = scopedOrg?.name || getSchoolName(user);
  const orgUuid = scopedOrgUuid;

  // Department list (the manager's school) feeds the assign-dropdown AND the
  // Departments management card; reloadable so CRUD there refreshes both.
  const loadDepartments = useCallback(
    (signal) => {
      if (!orgUuid) {
        setDepartments([]);
        return;
      }
      getDepartments({ params: { organization: orgUuid }, signal })
        .then((d) => setDepartments(Array.isArray(d) ? d : []))
        .catch((err) => {
          if (reportLoadError(err, t)) setDepartments([]);
        });
    },
    [orgUuid, t],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadDepartments(controller.signal);
    return () => controller.abort();
  }, [loadDepartments]);

  // Subject catalog feeds the teaching-subjects picker on the create/edit forms.
  useEffect(() => {
    const controller = new AbortController();
    getDomainSubjects({ params: { is_active: true }, signal: controller.signal })
      .then((subs) => setSubjects(Array.isArray(subs) ? subs : []))
      .catch((err) => {
        if (reportLoadError(err, t)) setSubjects([]);
      });
    return () => controller.abort();
  }, [t]);

  // Role catalog drives checkboxes, the filter, and chip labels. `name` is
  // localized server-side, so refetch when the UI language changes.
  useEffect(() => {
    const controller = new AbortController();
    getRoles({ signal: controller.signal })
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch((err) => reportLoadError(err, t));
    return () => controller.abort();
    // `resolvedLanguage`, not `language`: the latter can hold a tag i18next
    // rejected, which would refetch on a change the UI never actually made.
    // In practice this dependency rarely fires on its own — App.js remounts the
    // routed tree on a language switch, so the effect re-runs as a fresh mount
    // anyway. Kept because Role.name is translated server-side and this is what
    // states that dependency out loud.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  const roleNameByCode = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.code, r.name])),
    [roles],
  );

  const roleFilterOptions = useMemo(
    () => [
      { value: "", label: t("users.allRoles") },
      ...roles.map((r) => ({ value: r.code, label: r.name })),
    ],
    [roles, t],
  );

  const loadUsers = useCallback(
    (signal) => {
      setIsLoading(true);
      getUsers({
        params: {
          search: debouncedSearch,
          role: roleFilter || undefined,
          page,
          // Only when a school is actually picked: unscoped still means
          // "everyone I can see", which is the right answer for the network
          // rollup and for every single-school manager.
          organization: scopeMode === "school" ? scopedOrgUuid : undefined,
        },
        signal,
      })
        .then((data) => {
          const results = data?.results || [];
          const count = data?.count ?? 0;
          if (clampPageOnEmpty({ results, count, page, setPage })) return;
          setUsers(results);
          setTotal(count);
          // A ticked row can lose its email between loads; its box then
          // renders as "—" and can't be unticked by hand, so drop it here —
          // but only for rows this page actually shows; selections on other
          // pages are not evidence.
          setSelected((prev) => {
            const stale = results.filter(
              (u) => prev.has(u.uuid) && !isSelectable(u, user?.uuid),
            );
            if (stale.length === 0) return prev;
            const next = new Set(prev);
            stale.forEach((u) => next.delete(u.uuid));
            return next;
          });
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          toast.error(t("users.loadFailed"));
        })
        .finally(() => setIsLoading(false));
    },
    [debouncedSearch, roleFilter, page, t, user?.uuid, scopeMode, scopedOrgUuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadUsers(controller.signal);
    return () => controller.abort();
  }, [loadUsers]);

  // Selection is meaningless across schools, so a scope change drops it.
  useEffect(() => {
    setSelected(new Set());
    setAllPending(false);
  }, [scopedOrgUuid]);

  const toggleRow = (uuid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const togglePage = (uuids, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      uuids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const clearSelection = () => {
    setSelected(new Set());
    setAllPending(false);
  };

  // Bulk invite for the ticked rows (or, in allPending mode, everyone still
  // without a way to sign in — what a staff import leaves behind). Idempotent
  // on the backend — a live link is never revoked by a re-click. `organization`
  // pins a multi-school admin (and the superuser) to the school this page is
  // scoped to.
  const handleInviteSelected = useCallback(async () => {
    setInvitingAll(true);
    try {
      const data = await inviteAllUsers({
        organization: scopedOrgUuid,
        users: allPending ? undefined : Array.from(selected),
      });
      const invited = data?.invited ?? 0;
      if (invited > 0) {
        toast.success(t("users.inviteAllDone", { count: invited }));
      } else {
        toast.info(t("users.inviteAllNone"));
      }
      if ((data?.skipped_privileged ?? 0) > 0) {
        toast.info(t("users.inviteAllSkipped", { count: data.skipped_privileged }));
      }
      if (!allPending) {
        // A shortfall means the backend could not act on some ticked rows
        // (outside the writable scope, an SSO account — indistinguishable from
        // a password holder client-side — or the state moved underneath us).
        // Saying so beats a silent forever-retry.
        const missed =
          selected.size - (data?.invited ?? 0) - (data?.skipped_privileged ?? 0);
        if (missed > 0) {
          toast.info(t("users.inviteSelectedMissed", { count: missed }));
        }
      }
      setSelected(new Set());
      setAllPending(false);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err, t("users.inviteAllFailed")));
    } finally {
      setInvitingAll(false);
    }
  }, [t, loadUsers, scopedOrgUuid, selected, allPending]);

  // Per-row invite — also the only administrative password reset. Refetch on
  // success: the invite-state chip must reflect this click's outcome, or a
  // stale "send_failed" reads as "it failed again" and invites a second click
  // that revokes the link the first one just sent.
  const handleResendInvite = useCallback(
    async (target) => {
      setInvitingUuid(target.uuid);
      try {
        await resendInvitation({ uuid: target.uuid });
        toast.success(t("users.inviteSent", { name: fullName(target) }));
        loadUsers();
      } catch (err) {
        toast.error(formatApiError(err, t("users.inviteFailed")));
      } finally {
        setInvitingUuid(null);
      }
    },
    [t, loadUsers],
  );

  // A new search/filter resets to page 1 (avoids landing on an out-of-range page).
  // It also disarms allPending: that mode means "everyone pending in the
  // school", and leaving it armed over a freshly filtered list would misread
  // as "everyone I'm looking at". Hand-ticked rows survive — they are explicit.
  useEffect(() => {
    setPage(1);
    setAllPending(false);
  }, [debouncedSearch, roleFilter, scopedOrgUuid]);

  const totalPages = Math.max(1, Math.ceil(total / USERS_PAGE_SIZE));
  const isFiltered = Boolean(debouncedSearch) || Boolean(roleFilter);

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <BackLink />

        {/* Page header: title + school on the left, primary actions on the right. */}
        <div className="mt-4 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">
              {t("users.usersHeading")}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {/* Under a search or role filter `total` is the number of
                  matches, not the size of the school — labelling it
                  "N members" there states a headcount that is not true. */}
              {isFiltered
                ? t("users.headerSubFiltered", { count: total })
                : isNetworkScope
                ? t("users.headerSubNetwork", { count: total })
                : t("users.headerSub", { count: total, school })}
            </p>
          </div>
          {canManage && (
            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={() => setShowImport(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 transition"
              >
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
                {t("users.import.button")}
              </button>
              <button
                type="button"
                onClick={handleInviteSelected}
                disabled={invitingAll || (!allPending && selected.size === 0)}
                title={
                  !allPending && selected.size === 0
                    ? t("users.inviteSelectedHint")
                    : undefined
                }
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {invitingAll
                  ? t("users.inviteAllSending")
                  : allPending
                    ? t("users.inviteAllPending")
                    : t("users.inviteSelected", { count: selected.size })}
              </button>
              <button
                type="button"
                onClick={() => setShowDepartments(true)}
                disabled={isNetworkScope}
                title={isNetworkScope ? t("users.pickSchoolFirst") : undefined}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14-4H5m14 8H5m14 4H5" />
                </svg>
                {t("users.manageDepartments")}
              </button>
              <button
                type="button"
                onClick={() => setShowAddUser(true)}
                disabled={isNetworkScope}
                title={isNetworkScope ? t("users.pickSchoolFirst") : undefined}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-lg shadow-indigo-900/10 hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                {t("users.addUser")}
              </button>
            </div>
          )}
        </div>

        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-base font-bold text-slate-900">
              {t("users.usersHeading")}{" "}
              <span className="text-slate-400 font-normal">({total})</span>
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  {SearchIcon}
                </span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("common.search")}
                  className="w-48 pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition"
                />
              </div>
              <FilterSelect
                options={roleFilterOptions}
                value={roleFilter}
                onChange={setRoleFilter}
                icon={FilterIcon}
                ariaLabel={t("users.role")}
                minWidthClass="min-w-[9rem]"
              />
            </div>
          </div>

          {canManage && !isNetworkScope && (allPending || selected.size > 0) && (
            <div className="px-6 py-2.5 border-b border-indigo-100 bg-indigo-50/60 flex items-center gap-3 flex-wrap text-xs font-semibold text-indigo-900">
              <span>
                {allPending
                  ? t("users.selectionAllPending")
                  : t("users.selectionCount", { count: selected.size })}
              </span>
              {/* The pagination escape hatch: ticking offers the whole school,
                  so nobody walks N pages to invite everyone. Only over an
                  UNFILTERED list — this selects everyone pending in the school,
                  and offering that over a teachers-only view would invite
                  people the admin never saw. */}
              {!allPending &&
                total > users.length &&
                !debouncedSearch &&
                !roleFilter && (
                <button
                  type="button"
                  onClick={() => setAllPending(true)}
                  className="text-indigo-600 underline underline-offset-2 hover:text-indigo-800"
                >
                  {t("users.selectAllPending")}
                </button>
              )}
              <button
                type="button"
                onClick={clearSelection}
                className="ml-auto text-slate-500 hover:text-slate-700"
              >
                {t("users.clearSelection")}
              </button>
            </div>
          )}

          <UserTable
            users={users}
            isLoading={isLoading}
            canManage={canManage}
            showSchool={isNetworkScope}
            roleNameByCode={roleNameByCode}
            onEdit={setEditing}
            onDelete={setDeleting}
            onResendInvite={handleResendInvite}
            invitingUuid={invitingUuid}
            selectedSet={selected}
            selfUuid={user?.uuid}
            allPending={allPending}
            onToggleRow={toggleRow}
            onTogglePage={togglePage}
          />

          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500">
                {t("users.pageInfo", {
                  from: (page - 1) * USERS_PAGE_SIZE + 1,
                  to: Math.min(page * USERS_PAGE_SIZE, total),
                  total,
                })}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("users.prev")}
                </button>
                <span className="px-2 text-xs font-semibold text-slate-500">
                  {t("users.pageOf", { page, totalPages })}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isLoading}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("users.next")}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {showImport && (
        <StaffImportModal
          onClose={() => setShowImport(false)}
          onImported={() => loadUsers()}
        />
      )}

      {showAddUser && (
        <CreateUserModal
          onClose={() => setShowAddUser(false)}
          onCreated={() => loadUsers()}
          school={school}
          orgUuid={orgUuid}
          roles={roles}
          canManageRoles={canManageRoles}
          roleNameByCode={roleNameByCode}
          departments={departments}
          subjects={subjects}
        />
      )}

      {showDepartments && (
        <DepartmentsModal
          onClose={() => setShowDepartments(false)}
          departments={departments}
          orgUuid={orgUuid}
          onChanged={() => loadDepartments()}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          roles={roles}
          canManageRoles={canManageRoles}
          departments={departments}
          subjects={subjects}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadUsers();
          }}
        />
      )}

      {deleting && (
        <DeleteConfirmModal
          user={deleting}
          onCancel={() => setDeleting(null)}
          onConfirmed={() => {
            setSelected((prev) => {
              const next = new Set(prev);
              next.delete(deleting.uuid);
              return next;
            });
            setDeleting(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
}

// Where each account is in the onboarding funnel (serialized only for callers
// with users.manage). `send_failed` is the state this column exists for: the
// backend emails after responding and swallows SMTP errors, so without it a
// failed delivery looked exactly like a sent invitation.
const INVITE_STATE_STYLES = {
  active: "bg-emerald-50 text-emerald-700",
  invited: "bg-indigo-50 text-indigo-600",
  send_failed: "bg-rose-50 text-rose-600",
  expired: "bg-amber-50 text-amber-700",
  not_invited: "bg-slate-100 text-slate-500",
};

function InviteStateChip({ state, managedBy }) {
  const { t } = useTranslation();
  if (!state) return <span className="text-slate-300">—</span>;
  // A directory account is "active" the same way a password account is, but
  // saying so the same way invites a "Send invite" click that only ends in
  // the federation-policy 400 — name the real access path instead.
  const label =
    state === "active" && managedBy
      ? t("users.inviteState.viaDirectory", { name: managedBy.name })
      : t(`users.inviteState.${state}`);
  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap ${
        INVITE_STATE_STYLES[state] || "bg-slate-100 text-slate-500"
      }`}
    >
      {label}
    </span>
  );
}

function UserTable({
  users,
  isLoading,
  canManage,
  showSchool,
  roleNameByCode,
  onEdit,
  onDelete,
  onResendInvite,
  invitingUuid,
  selectedSet,
  selfUuid,
  allPending,
  onToggleRow,
  onTogglePage,
}) {
  const { t } = useTranslation();
  const selectableOnPage = canManage && !showSchool
    ? users.filter((u) => isSelectable(u, selfUuid))
    : [];
  const allOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((u) => selectedSet.has(u.uuid));
  const someOnPageSelected = selectableOnPage.some((u) => selectedSet.has(u.uuid));
  if (isLoading && users.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-slate-400">
        {t("users.loading")}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-slate-400">
        {t("users.noUsers")}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-50">
            {canManage && (
              <th className="pl-6 pr-1 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label={t("users.selectPage")}
                  title={
                    selectableOnPage.length === 0
                      ? t("users.selectPageEmpty")
                      : undefined
                  }
                  checked={allPending || allOnPageSelected}
                  disabled={allPending || selectableOnPage.length === 0}
                  ref={(el) => {
                    if (el)
                      el.indeterminate =
                        !allPending && someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={(e) =>
                    onTogglePage(
                      selectableOnPage.map((u) => u.uuid),
                      e.target.checked,
                    )
                  }
                  className="h-4 w-4 rounded border-slate-400 text-indigo-600 focus:ring-indigo-500 disabled:border-slate-200 disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
              </th>
            )}
            <th className="px-6 py-3">{t("users.colName")}</th>
            <th className="px-6 py-3">{t("users.colEmail")}</th>
            <th className="px-6 py-3">{t("users.colRole")}</th>
            {canManage && <th className="px-6 py-3">{t("users.colInviteState")}</th>}
            {canManage && <th className="px-6 py-3 text-right">{t("users.colActions")}</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.uuid}
              className="border-t border-slate-100 hover:bg-slate-50"
            >
              {canManage && (
                <td className="pl-6 pr-1 py-3">
                  {isSelectable(u, selfUuid) ? (
                    <input
                      type="checkbox"
                      aria-label={t("users.selectRow", { name: fullName(u) })}
                      checked={allPending || selectedSet.has(u.uuid)}
                      disabled={allPending}
                      onChange={() => onToggleRow(u.uuid)}
                      className="h-4 w-4 rounded border-slate-400 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed"
                    />
                  ) : (
                    /* No email — nowhere to send anything. The table's usual
                       "—", not a disabled box: a pale dead checkbox reads as a
                       broken control. */
                    <span
                      className="text-slate-300 select-none"
                      title={t("users.inviteNoEmail")}
                    >
                      —
                    </span>
                  )}
                </td>
              )}
              <td className="px-6 py-3 font-medium text-slate-900">
                <span className="inline-flex items-center gap-2">
                  {fullName(u)}
                  {/* `managed_by` is null for a locally created account, so the
                      marker is only ever on the rows the directory owns. */}
                  {u.managed_by && (
                    <span
                      title={t("users.managedAccount", { name: u.managed_by.name })}
                      className="inline-flex items-center rounded-md bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700 whitespace-nowrap"
                    >
                      {t("users.fromDirectory", { name: u.managed_by.name })}
                    </span>
                  )}
                </span>
                {/* Which school this row belongs to. Only in a multi-school
                    view: repeating one name down every row of a single
                    school's roster is noise. */}
                {showSchool && u.organization?.name && (
                  <span className="block text-xs font-normal text-slate-400 truncate">
                    {u.organization.name}
                  </span>
                )}
              </td>
              <td className="px-6 py-3 text-slate-600">{u.email || "—"}</td>
              <td className="px-6 py-3">
                {(u.roles || []).length === 0 ? (
                  <span className="text-slate-300">—</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(u.roles || []).map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-[11px] font-semibold leading-none text-slate-600 whitespace-nowrap"
                      >
                        {roleNameByCode[code] || code}
                      </span>
                    ))}
                  </div>
                )}
              </td>
              {canManage && (
                <td className="px-6 py-3">
                  <InviteStateChip state={u.invite_state} managedBy={u.managed_by} />
                </td>
              )}
              {canManage && (
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onEdit(u)}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 mr-3"
                  >
                    {t("users.edit")}
                  </button>
                  {/* Also the only administrative password reset there is —
                      there's no self-service "forgot password" flow — so this
                      is what a "I can't get in" support request resolves to.
                      Not for a directory account: it has no local password to
                      set, and the backend refuses the same action with a 400. */}
                  <button
                    type="button"
                    disabled={!u.email || Boolean(u.managed_by) || invitingUuid === u.uuid}
                    onClick={() => onResendInvite(u)}
                    title={
                      u.managed_by
                        ? t("users.inviteManaged", { name: u.managed_by.name })
                        : u.email
                          ? undefined
                          : t("users.inviteNoEmail")
                    }
                    className="text-xs font-semibold text-slate-600 hover:text-slate-800 mr-3 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {invitingUuid === u.uuid
                      ? t("users.inviteSending")
                      : t("users.resendInvite")}
                  </button>
                  {/* A directory account is deleted by ending it at the source
                      — a local delete cannot be undone from any screen and the
                      next sync silently updates the soft-deleted row in place
                      rather than recreating the account. */}
                  <button
                    type="button"
                    disabled={Boolean(u.managed_by)}
                    onClick={() => onDelete(u)}
                    title={
                      u.managed_by
                        ? t("users.deleteManaged", { name: u.managed_by.name })
                        : undefined
                    }
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-rose-600"
                  >
                    {t("users.delete")}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Department <select> + teaching-subjects multi-select. Shown only for teacher
// accounts (department/subjects live on the teacher's school staff link).
function TeacherAssignmentFields({
  departments,
  subjects,
  department,
  onDepartment,
  selectedSubjects,
  onSubjects,
}) {
  const { t } = useTranslation();
  const subjectOptions = useMemo(
    () => subjects.map((s) => ({ value: s.uuid, label: s.name })),
    [subjects],
  );
  const departmentOptions = useMemo(
    () => [
      { value: "", label: t("users.noDepartment") },
      ...departments.map((d) => ({ value: d.uuid, label: d.name })),
    ],
    [departments, t],
  );
  return (
    <>
      <Field label={t("users.department")}>
        <FilterSelect
          options={departmentOptions}
          value={department}
          onChange={onDepartment}
          ariaLabel={t("users.department")}
          minWidthClass="w-full"
          className="w-full"
        />
      </Field>
      <Field label={t("users.teachingSubjects")}>
        <MultiSelect
          options={subjectOptions}
          value={selectedSubjects}
          onChange={onSubjects}
          placeholder={t("users.subjectsPlaceholder")}
          emptyText={t("common.subjectsNoMatch")}
        />
      </Field>
    </>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
  school,
  orgUuid,
  roles,
  canManageRoles,
  roleNameByCode,
  departments,
  subjects,
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      roles: [ROLE_CODES.TEACHER],
    },
  });
  const [department, setDepartment] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const isTeacher = (watch("roles") || []).includes(ROLE_CODES.TEACHER);

  const onSubmit = async (data) => {
    if (!data.roles?.length) {
      toast.error(t("users.roleRequired"));
      return;
    }
    const body = { ...data };
    // Say which school explicitly. The backend infers it for a single-school
    // manager but refuses to guess for anyone who administers more than one
    // (and for a superuser), which is how "Add user" used to end in a 400 that
    // named a field the form never showed.
    if (orgUuid) body.organization = orgUuid;
    if (data.roles.includes(ROLE_CODES.TEACHER)) {
      body.department = department || null;
      body.subjects = selectedSubjects;
    }
    try {
      const created = await createUser({ body });
      const codes = created?.roles?.length ? created.roles : data.roles;
      const label = codes.map((c) => roleNameByCode[c] || c).join(", ");
      const name = fullName(created) || `${data.first_name} ${data.last_name}`;
      toast.success(
        t("users.createdToast", { name, role: label, email: data.email }),
      );
      reset();
      setDepartment("");
      setSelectedSubjects([]);
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err, t("users.createFailed")));
    }
  };

  return (
    <ModalShell
      title={t("users.addUser")}
      subtitle={
        <>
          {t("users.assignedTo")}{" "}
          <span className="font-bold text-slate-700">{school}</span>
        </>
      }
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="space-y-4">
          <Field label={t("users.firstName")} error={errors.first_name?.message} required>
          <input
            type="text"
            autoComplete="given-name"
            disabled={isSubmitting}
            {...register("first_name", {
              required: t("users.firstNameRequired"),
              maxLength: { value: 64, message: t("users.max64") },
            })}
            className={inputClass(errors.first_name)}
            placeholder={t("users.firstNamePlaceholder")}
          />
        </Field>

        <Field label={t("users.lastName")} error={errors.last_name?.message} required>
          <input
            type="text"
            autoComplete="family-name"
            disabled={isSubmitting}
            {...register("last_name", {
              required: t("users.lastNameRequired"),
              maxLength: { value: 64, message: t("users.max64") },
            })}
            className={inputClass(errors.last_name)}
            placeholder={t("users.lastNamePlaceholder")}
          />
        </Field>

        <Field label={t("users.email")} error={errors.email?.message} required>
          <input
            type="email"
            autoComplete="email"
            disabled={isSubmitting}
            {...register("email", {
              required: t("users.emailRequired"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("users.validEmail"),
              },
            })}
            className={inputClass(errors.email)}
            placeholder={t("users.emailPlaceholder")}
          />
        </Field>

        <RoleCheckboxGrid
          register={register}
          roles={roles}
          canManageRoles={canManageRoles}
          disabled={isSubmitting}
        />

          {isTeacher && (
            <TeacherAssignmentFields
              departments={departments}
              subjects={subjects}
              department={department}
              onDepartment={setDepartment}
              selectedSubjects={selectedSubjects}
              onSubjects={setSelectedSubjects}
            />
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-lg shadow-indigo-900/10 hover:bg-indigo-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t("users.creating") : t("users.createUser")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// Manager-facing CRUD for the school's departments. Backend scopes writes to
// the manager's own org; `onChanged` refreshes the parent's department list so
// the assign-dropdown stays in sync.
function DepartmentsModal({ onClose, departments, orgUuid, onChanged }) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await createDepartment({ body: { organization: orgUuid, name } });
      setNewName("");
      toast.success(t("users.deptAdded", { name }));
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, t("users.deptError")));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (d) => {
    const name = draft.trim();
    if (!name || name === d.name) {
      setEditingId(null);
      return;
    }
    setBusy(true);
    try {
      await updateDepartment({ uuid: d.uuid, body: { name } });
      setEditingId(null);
      toast.success(t("users.deptUpdated", { name }));
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, t("users.deptError")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    setBusy(true);
    try {
      await deleteDepartment({ uuid: d.uuid });
      setConfirmId(null);
      toast.success(t("users.deptDeleted", { name: d.name }));
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, t("users.deptError")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={t("users.departments")}
      subtitle={t("users.departmentsSub")}
      onClose={onClose}
      maxWidth="max-w-md"
    >
      <div className="space-y-2">
        {departments.length === 0 && (
          <p className="text-sm text-slate-400">{t("users.noDepartments")}</p>
        )}
        {departments.map((d) =>
          editingId === d.uuid ? (
            <div key={d.uuid} className="flex items-center gap-2">
              <input
                autoFocus
                className={inputClass()}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && rename(d)}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => rename(d)}
                className="shrink-0 text-xs font-bold text-indigo-600 hover:text-indigo-700"
              >
                {t("common.save")}
              </button>
              <button
                type="button"
                onClick={() => setEditingId(null)}
                className="shrink-0 text-xs font-semibold text-slate-400 hover:text-slate-600"
              >
                {t("common.cancel")}
              </button>
            </div>
          ) : (
            <div
              key={d.uuid}
              className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2"
            >
              {confirmId === d.uuid ? (
                <>
                  <span className="flex-1 text-sm text-slate-600 truncate">
                    {t("users.deleteDeptConfirm")}
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => remove(d)}
                    className="shrink-0 text-xs font-bold text-rose-600 hover:text-rose-700"
                  >
                    {t("users.delete")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmId(null)}
                    className="shrink-0 text-xs font-semibold text-slate-400 hover:text-slate-600"
                  >
                    {t("common.cancel")}
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-semibold text-slate-700 truncate">
                    {d.name}
                  </span>
                  <button
                    type="button"
                    aria-label={t("users.rename")}
                    onClick={() => {
                      setEditingId(d.uuid);
                      setDraft(d.name);
                    }}
                    className="shrink-0 text-slate-400 hover:text-indigo-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label={t("users.delete")}
                    onClick={() => setConfirmId(d.uuid)}
                    className="shrink-0 text-slate-400 hover:text-rose-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          ),
        )}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input
          className={inputClass()}
          placeholder={t("users.newDepartmentPlaceholder")}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button
          type="button"
          disabled={busy || !newName.trim()}
          onClick={add}
          className="shrink-0 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("users.addDepartment")}
        </button>
      </div>
    </ModalShell>
  );
}

function EditUserModal({
  user,
  roles,
  canManageRoles,
  departments,
  subjects,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      roles: Array.isArray(user.roles) ? user.roles : [],
      is_active: user.is_active,
      can_be_evaluated: user.can_be_evaluated ?? true,
    },
  });
  // Pre-fill from the list payload, keyed on uuids (the source of truth the
  // form also writes) — never name-matching, which could silently wipe.
  const [department, setDepartment] = useState(() => user.department?.uuid || "");
  const [selectedSubjects, setSelectedSubjects] = useState(
    () => (user.subjects || []).map((s) => s.uuid ?? s),
  );

  // Null for a locally created account — the form then behaves exactly as it
  // always has. `managed_fields` names the concrete fields bilorg maintains.
  const directory = user.managed_by || null;
  const managed = useMemo(
    () => new Set(user.managed_fields || []),
    [user.managed_fields],
  );

  const privilegedCodes = useMemo(
    () => new Set(roles.filter((r) => r.is_privileged).map((r) => r.code)),
    [roles],
  );

  // Grants this screen cannot revoke, marked with where they come from. A
  // directory grant is removed in the provider's role_map; a platform-wide
  // grant is not a school link at all, and `set_roles_for_links` only ever
  // touches links — so unticking either one would be a silent no-op.
  const lockedRoles = useMemo(() => {
    const locks = new Map();
    (user.platform_roles || []).forEach((code) =>
      locks.set(code, {
        tag: t("users.rolePlatformTag"),
        title: t("users.roleFromPlatform"),
      }),
    );
    // Directory second: a role held from BOTH sides is still bilorg's to
    // revoke, so that is the reason the reader needs to see.
    if (directory) {
      (user.directory_roles || []).forEach((code) =>
        locks.set(code, {
          tag: directory.name,
          title: t("users.roleFromDirectory", { name: directory.name }),
        }),
      );
    }
    return locks;
  }, [user.platform_roles, user.directory_roles, directory, t]);

  // Locked checkboxes are `disabled`, which react-hook-form omits from its
  // value — so what the account actually keeps is the ticked set plus every
  // locked role it already holds. Both the teacher-only fields below and the
  // payload read this, or locking a directory-granted `teacher` would hide the
  // assignment fields and drop can_be_evaluated.
  const heldLocked = (user.roles || []).filter(
    (code) => lockedRoles.has(code) || (!canManageRoles && privilegedCodes.has(code)),
  );
  const effectiveRoles = Array.from(
    new Set([...(watch("roles") || []), ...heldLocked]),
  );
  const isTeacher = effectiveRoles.includes(ROLE_CODES.TEACHER);

  const onSubmit = async (data) => {
    const body = {
      ...data,
      roles: Array.from(new Set([...(data.roles || []), ...heldLocked])),
    };
    // The directory owns these; sending them back is a write the API rejects
    // with a 400 naming bilorg, and the inputs above are read-only anyway.
    ACCOUNT_FIELDS.forEach((field) => {
      if (managed.has(field)) delete body[field];
    });
    if (!body.roles?.length) {
      toast.error(t("users.roleRequired"));
      return;
    }
    if (!body.roles.includes(ROLE_CODES.TEACHER)) {
      // The capability flag belongs to the teacher profile; don't create one
      // for non-teachers as a side effect.
      delete body.can_be_evaluated;
    } else {
      body.department = department || null;
      body.subjects = selectedSubjects;
    }
    try {
      await updateUser({ uuid: user.uuid, body });
      toast.success(t("users.updatedToast", { name: fullName({ ...user, ...data }) }));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err, t("users.updateFailed")));
    }
  };

  return (
    <ModalShell
      onClose={onClose}
      title={t("users.editUser")}
      subtitle={
        directory ? t("users.managedAccount", { name: directory.name }) : undefined
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <EditAccountField
          label={t("users.firstName")}
          name="first_name"
          rules={{ required: t("users.required") }}
          error={errors.first_name}
          register={register}
          directory={managed.has("first_name") ? directory : null}
        />
        <EditAccountField
          label={t("users.lastName")}
          name="last_name"
          rules={{ required: t("users.required") }}
          error={errors.last_name}
          register={register}
          directory={managed.has("last_name") ? directory : null}
        />
        <EditAccountField
          label={t("users.email")}
          name="email"
          type="email"
          rules={{
            required: t("users.required"),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t("users.invalidEmail"),
            },
          }}
          error={errors.email}
          register={register}
          directory={managed.has("email") ? directory : null}
        />
        <RoleCheckboxGrid
          register={register}
          roles={roles}
          canManageRoles={canManageRoles}
          disabled={isSubmitting}
          lockedRoles={lockedRoles}
          idPrefix="edit-role"
          paddingClass="py-2.5"
        />
        {isTeacher && (
          <>
            <TeacherAssignmentFields
              departments={departments}
              subjects={subjects}
              department={department}
              onDepartment={setDepartment}
              selectedSubjects={selectedSubjects}
              onSubjects={setSelectedSubjects}
            />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" {...register("can_be_evaluated")} />
              {t("users.canBeEvaluated")}
            </label>
          </>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" {...register("is_active")} />
          {t("users.active")}
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            {isSubmitting ? t("users.saving") : t("users.save")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteConfirmModal({ user, onCancel, onConfirmed }) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const confirm = async () => {
    setIsDeleting(true);
    try {
      await deleteUser({ uuid: user.uuid });
      toast.success(t("users.removedToast", { name: fullName(user) }));
      onConfirmed?.();
    } catch (_e) {
      toast.error(t("users.deleteFailed"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ModalShell onClose={onCancel} title={t("users.deleteUser")}>
      <p className="text-sm text-slate-600">
        {t("users.deleteConfirm", { name: fullName(user) })}
      </p>
      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isDeleting}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={isDeleting}
          className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-60"
        >
          {isDeleting ? t("users.removing") : t("users.delete")}
        </button>
      </div>
    </ModalShell>
  );
}


